import React from 'react';
import {
  Heading,
  Link,
  ListItem,
  Text,
  UnorderedList,
  useColorModeValue,
  VStack
} from '@chakra-ui/react';
import { LINK_COLOR } from '../constants';

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }): JSX.Element {
  return (
    <Link href={href} color={LINK_COLOR} isExternal wordBreak="break-all">
      {children}
    </Link>
  );
}

interface SectionHeadingProps {
  children: React.ReactNode;
  color: string;
}

function SectionHeading({ children, color }: SectionHeadingProps): JSX.Element {
  return (
    <Heading as="h2" size="md" color={color} mt={2}>
      {children}
    </Heading>
  );
}

function PrivacyPolicyContent(): JSX.Element {
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('green.700', 'green.300');

  return (
    <VStack spacing={6} align="stretch" color={textColor}>
      <SectionHeading color={headingColor}>1. Responsable del tratamiento</SectionHeading>
      <Text textAlign="justify">
        El responsable del tratamiento de los datos personales relacionados con esta aplicación
        es <Text as="span" fontWeight="semibold">Club Atlético Pacífico</Text> (en adelante, «el
        Club»).
      </Text>
      <UnorderedList spacing={1} pl={2}>
        <ListItem>
          <Text as="span" fontWeight="semibold">Domicilio:</Text> Charlone 285, Bahía Blanca (C.P. 8000), Provincia de Buenos Aires, Argentina
        </ListItem>
        <ListItem>
          <Text as="span" fontWeight="semibold">Correo electrónico de contacto:</Text> clubpacificobahia@gmail.com
        </ListItem>
        <ListItem>
          <Text as="span" fontWeight="semibold">Sitio web:</Text>{' '}
          <ExternalLink href="https://pacificobb.com">https://pacificobb.com</ExternalLink>
        </ListItem>
      </UnorderedList>
      <Text textAlign="justify">
        Esta política describe cómo se tratan los datos personales en el marco de la aplicación
        utilizada por el Club para gestionar el envío de comunicaciones a sus socios mediante
        WhatsApp.
      </Text>

      <SectionHeading color={headingColor}>2. Finalidad de la aplicación</SectionHeading>
      <Text textAlign="justify">
        La aplicación tiene como finalidad facilitar las comunicaciones entre el Club y sus socios
        a través de WhatsApp, incluyendo:
      </Text>
      <UnorderedList spacing={1} pl={2}>
        <ListItem>El envío de recordatorios sobre el pago de cuotas sociales.</ListItem>
        <ListItem>
          La comunicación de información relacionada con la situación de las cuotas y la membresía.
        </ListItem>
        <ListItem>
          El envío de enlaces o instrucciones para facilitar el pago de las cuotas.
        </ListItem>
        <ListItem>La gestión y el seguimiento del estado de las comunicaciones enviadas.</ListItem>
        <ListItem>
          La atención de consultas y respuestas relacionadas con dichas comunicaciones, cuando
          corresponda.
        </ListItem>
      </UnorderedList>
      <Text textAlign="justify">
        La aplicación no tiene como finalidad comercializar datos personales ni utilizarlos para
        publicidad de terceros.
      </Text>
      <Text textAlign="justify">
        Cualquier utilización de los datos para finalidades adicionales requerirá una base jurídica
        válida y, cuando corresponda, información previa al interesado.
      </Text>

      <SectionHeading color={headingColor}>3. Datos personales tratados</SectionHeading>
      <Text textAlign="justify">
        En función de la comunicación y de las funcionalidades utilizadas, la aplicación podrá
        tratar las siguientes categorías de datos personales:
      </Text>
      <Text fontWeight="semibold" color={headingColor}>
        Datos identificativos y de contacto
      </Text>
      <UnorderedList spacing={1} pl={2}>
        <ListItem>Número de teléfono asociado a la cuenta de WhatsApp del socio.</ListItem>
        <ListItem>Identificador interno del socio en los sistemas del Club.</ListItem>
      </UnorderedList>
      <Text fontWeight="semibold" color={headingColor}>
        Datos relacionados con la membresía
      </Text>
      <UnorderedList spacing={1} pl={2}>
        <ListItem>
          Información necesaria para identificar la membresía y gestionar las comunicaciones sobre
          las cuotas sociales.
        </ListItem>
        <ListItem>
          Estado de las cuotas, cuando sea necesario para generar un recordatorio.
        </ListItem>
        <ListItem>
          Información estrictamente necesaria para facilitar el pago, como un enlace de pago,
          cuando corresponda.
        </ListItem>
      </UnorderedList>
      <Text fontWeight="semibold" color={headingColor}>
        Datos técnicos y de comunicación
      </Text>
      <UnorderedList spacing={1} pl={2}>
        <ListItem>Fecha y hora de los envíos.</ListItem>
        <ListItem>
          Estado de los mensajes, como enviado, entregado, leído o fallido, cuando dicha
          información esté disponible.
        </ListItem>
        <ListItem>
          Respuestas recibidas y otra información necesaria para gestionar la conversación, cuando
          corresponda.
        </ListItem>
      </UnorderedList>
      <Text textAlign="justify">
        El Club procurará limitar el tratamiento a los datos adecuados, pertinentes y necesarios
        para las finalidades descritas en esta política.
      </Text>

      <SectionHeading color={headingColor}>4. Origen de los datos y base jurídica</SectionHeading>
      <Text textAlign="justify">
        Los datos personales proceden principalmente de la información facilitada por los socios
        al registrarse o actualizar sus datos en los sistemas del Club, así como de los registros
        administrativos relacionados con su membresía.
      </Text>
      <Text textAlign="justify">
        El tratamiento se realizará sobre la base jurídica que corresponda en cada caso, que podrá
        incluir:
      </Text>
      <UnorderedList spacing={1} pl={2}>
        <ListItem>
          La ejecución de la relación asociativa y la gestión de las obligaciones relacionadas con
          la membresía.
        </ListItem>
        <ListItem>El cumplimiento de obligaciones legales aplicables.</ListItem>
        <ListItem>
          El consentimiento del interesado, cuando sea necesario para el envío de comunicaciones
          por WhatsApp o para otras finalidades que lo requieran.
        </ListItem>
        <ListItem>
          El interés legítimo del Club, cuando resulte aplicable, tras la correspondiente
          evaluación y respetando los derechos y libertades de los interesados.
        </ListItem>
      </UnorderedList>
      <Text textAlign="justify">
        El Club respetará las condiciones de uso de WhatsApp Business y los requisitos aplicables
        a las comunicaciones iniciadas por la organización, incluidos los relativos al
        consentimiento previo cuando corresponda.
      </Text>
      <Text textAlign="justify">
        Los socios podrán solicitar información sobre la base jurídica utilizada para el
        tratamiento de sus datos.
      </Text>

      <SectionHeading color={headingColor}>5. Uso de WhatsApp y servicios de Meta</SectionHeading>
      <Text textAlign="justify">
        Para enviar y gestionar las comunicaciones, el Club utiliza la plataforma WhatsApp Business
        Platform, mediante la API de WhatsApp Cloud proporcionada por Meta.
      </Text>
      <Text textAlign="justify">
        En este contexto, determinados datos, como el número de teléfono, el contenido de los
        mensajes y los metadatos técnicos necesarios para su transmisión, podrán ser tratados por
        Meta y por los proveedores que intervengan en la prestación del servicio, conforme a sus
        respectivas condiciones y políticas de privacidad.
      </Text>
      <Text textAlign="justify">
        El uso de WhatsApp está sujeto también a las condiciones y políticas de privacidad de
        dicha plataforma:
      </Text>
      <Text>
        <ExternalLink href="https://www.whatsapp.com/legal/privacy-policy">
          https://www.whatsapp.com/legal/privacy-policy
        </ExternalLink>
      </Text>
      <Text textAlign="justify">Política de privacidad de Meta:</Text>
      <Text>
        <ExternalLink href="https://www.facebook.com/privacy/policy/">
          https://www.facebook.com/privacy/policy/
        </ExternalLink>
      </Text>
      <Text textAlign="justify">
        Meta y los demás proveedores que intervengan podrán tratar datos para prestar, proteger y
        mantener sus servicios, de acuerdo con sus propias condiciones y obligaciones legales.
      </Text>
      <Text textAlign="justify">
        El Club no controla las prácticas de privacidad independientes de dichos proveedores. Para
        obtener información sobre ellas, el interesado deberá consultar las políticas
        correspondientes.
      </Text>

      <SectionHeading color={headingColor}>6. Destinatarios y comunicación de datos</SectionHeading>
      <Text textAlign="justify">Los datos personales podrán ser accesibles para:</Text>
      <UnorderedList spacing={1} pl={2}>
        <ListItem>
          Personal autorizado del Club que necesite acceder a ellos para gestionar las membresías
          y las comunicaciones.
        </ListItem>
        <ListItem>
          Meta y WhatsApp, en la medida necesaria para prestar el servicio de mensajería.
        </ListItem>
        <ListItem>
          Proveedores tecnológicos que participen en el alojamiento, mantenimiento, seguridad o
          funcionamiento de los sistemas del Club, cuando corresponda.
        </ListItem>
        <ListItem>
          Autoridades públicas y organismos competentes cuando exista una obligación legal de
          comunicación.
        </ListItem>
      </UnorderedList>
      <Text textAlign="justify">
        El Club no vende datos personales a terceros ni los cede para que estos realicen
        publicidad propia.
      </Text>
      <Text textAlign="justify">
        Cuando intervengan proveedores que traten datos por cuenta del Club, se adoptarán las
        medidas contractuales y organizativas que resulten exigibles.
      </Text>

      <SectionHeading color={headingColor}>7. Transferencias internacionales de datos</SectionHeading>
      <Text textAlign="justify">
        Al utilizar servicios tecnológicos de terceros, determinados datos personales podrían ser
        tratados o almacenados fuera del país en el que se encuentra el Club o el interesado.
      </Text>
      <Text textAlign="justify">
        Cuando se produzcan transferencias internacionales de datos sujetas a requisitos legales
        específicos, el Club procurará que se realicen con las garantías adecuadas y de
        conformidad con la normativa aplicable, incluyendo, cuando corresponda, las decisiones de
        adecuación u otros mecanismos legalmente reconocidos.
      </Text>
      <Text textAlign="justify">
        Para obtener información adicional sobre las garantías aplicables a una transferencia
        concreta, los interesados podrán contactar con el Club a través de los medios indicados en
        esta política.
      </Text>

      <SectionHeading color={headingColor}>8. Plazo de conservación</SectionHeading>
      <Text textAlign="justify">
        Los datos personales se conservarán únicamente durante el tiempo necesario para cumplir
        las finalidades descritas en esta política y atender las obligaciones legales aplicables.
      </Text>
      <Text textAlign="justify">En particular:</Text>
      <UnorderedList spacing={1} pl={2}>
        <ListItem>
          Los datos de contacto se conservarán mientras sean necesarios para gestionar la relación
          asociativa y las comunicaciones correspondientes.
        </ListItem>
        <ListItem>
          Los registros de envío y entrega se conservarán durante el tiempo necesario para la
          trazabilidad, la resolución de incidencias y la seguridad del servicio.
        </ListItem>
        <ListItem>
          Los datos relacionados con cuotas y pagos se conservarán conforme a las necesidades de
          gestión administrativa y a los plazos legales aplicables.
        </ListItem>
        <ListItem>
          Los datos que deban conservarse para atender reclamaciones o responsabilidades legales
          podrán mantenerse durante los plazos de prescripción correspondientes.
        </ListItem>
      </UnorderedList>
      <Text textAlign="justify">
        Una vez que los datos dejen de ser necesarios, se eliminarán o anonimizarán de forma
        segura, salvo que exista una obligación legal que exija su conservación.
      </Text>

      <SectionHeading color={headingColor}>9. Seguridad de la información</SectionHeading>
      <Text textAlign="justify">
        El Club adoptará medidas técnicas y organizativas razonables para proteger los datos
        personales frente a accesos no autorizados, pérdida, alteración, divulgación o destrucción
        accidental.
      </Text>
      <Text textAlign="justify">
        Estas medidas podrán incluir controles de acceso, gestión de credenciales, limitación de
        permisos, protección de los sistemas y revisión de los registros técnicos.
      </Text>
      <Text textAlign="justify">
        El acceso a los datos estará limitado a las personas y los proveedores que lo necesiten
        para las finalidades autorizadas.
      </Text>
      <Text textAlign="justify">
        Ningún sistema de transmisión o almacenamiento de información puede garantizar una
        seguridad absoluta; no obstante, el Club procurará mantener medidas de protección
        adecuadas a los riesgos del tratamiento.
      </Text>

      <SectionHeading color={headingColor}>10. Derechos de los interesados</SectionHeading>
      <Text textAlign="justify">
        Los interesados podrán ejercer los derechos que les reconozca la normativa aplicable,
        incluidos, cuando corresponda:
      </Text>
      <UnorderedList spacing={2} pl={2}>
        <ListItem>
          <Text as="span" fontWeight="semibold">Acceso:</Text> conocer si se están tratando sus
          datos personales y obtener información sobre dicho tratamiento.
        </ListItem>
        <ListItem>
          <Text as="span" fontWeight="semibold">Rectificación:</Text> solicitar la corrección de
          datos inexactos o incompletos.
        </ListItem>
        <ListItem>
          <Text as="span" fontWeight="semibold">Supresión:</Text> solicitar la eliminación de sus
          datos cuando se cumplan los requisitos legales.
        </ListItem>
        <ListItem>
          <Text as="span" fontWeight="semibold">Oposición:</Text> oponerse a determinados
          tratamientos de sus datos personales.
        </ListItem>
        <ListItem>
          <Text as="span" fontWeight="semibold">Limitación:</Text> solicitar la restricción del
          tratamiento en los supuestos legalmente previstos.
        </ListItem>
        <ListItem>
          <Text as="span" fontWeight="semibold">Portabilidad:</Text> recibir sus datos en un
          formato estructurado y solicitar su transmisión cuando resulte aplicable.
        </ListItem>
        <ListItem>
          <Text as="span" fontWeight="semibold">Retirada del consentimiento:</Text> retirar el
          consentimiento en cualquier momento cuando el tratamiento se base en él, sin que ello
          afecte a la licitud del tratamiento realizado previamente.
        </ListItem>
      </UnorderedList>
      <Text textAlign="justify">
        Para ejercer estos derechos, los interesados podrán contactar con el Club a través de
        [Correo electrónico de privacidad o contacto del Club], indicando el derecho que desean
        ejercer y aportando la información estrictamente necesaria para verificar su identidad.
      </Text>
      <Text textAlign="justify">
        El Club responderá a las solicitudes dentro de los plazos previstos por la normativa
        aplicable.
      </Text>
      <Text textAlign="justify">
        Cuando resulte aplicable el Reglamento General de Protección de Datos de la Unión Europea,
        los interesados también podrán presentar una reclamación ante la autoridad de protección
        de datos competente.
      </Text>

      <SectionHeading color={headingColor}>
        11. Preferencias y baja de las comunicaciones por WhatsApp
      </SectionHeading>
      <Text textAlign="justify">
        Los socios podrán solicitar que dejen de enviárseles comunicaciones por WhatsApp a través
        de los mecanismos habilitados por el Club, por ejemplo, respondiendo al mensaje con una
        solicitud de baja o contactando con el Club por los canales oficiales.
      </Text>
      <Text textAlign="justify">
        El Club atenderá las solicitudes de baja de acuerdo con las condiciones del servicio y la
        normativa aplicable.
      </Text>
      <Text textAlign="justify">
        La baja de las comunicaciones por WhatsApp no implica necesariamente la cancelación de la
        membresía ni la eliminación de los datos que el Club deba conservar para cumplir
        obligaciones legales o gestionar la relación asociativa. Cuando corresponda, el Club podrá
        seguir utilizando otros canales legítimos para comunicaciones necesarias relacionadas con
        dicha relación.
      </Text>

      <SectionHeading color={headingColor}>12. Datos de menores de edad</SectionHeading>
      <Text textAlign="justify">
        Cuando los sistemas del Club incluyan datos de socios menores de edad, el tratamiento se
        realizará conforme a las normas aplicables y teniendo en cuenta las medidas de protección
        específicas que correspondan.
      </Text>
      <Text textAlign="justify">
        Cuando resulte legalmente necesario, se obtendrá la autorización del padre, madre, tutor o
        representante legal antes de tratar los datos o utilizar canales de comunicación que
        requieran dicha autorización.
      </Text>

      <SectionHeading color={headingColor}>13. Modificaciones de esta política</SectionHeading>
      <Text textAlign="justify">
        El Club podrá actualizar esta política para reflejar cambios en la aplicación, las
        finalidades del tratamiento, los servicios utilizados o la normativa aplicable.
      </Text>
      <Text textAlign="justify">
        La versión vigente estará disponible públicamente en el sitio web del Club e indicará la
        fecha de su última actualización.
      </Text>
      <Text textAlign="justify">
        Cuando una modificación requiera información adicional a los interesados conforme a la
        normativa aplicable, el Club adoptará las medidas necesarias para comunicarla.
      </Text>

      <SectionHeading color={headingColor}>14. Contacto</SectionHeading>
      <Text textAlign="justify">
        Para consultas relacionadas con esta política, el tratamiento de datos personales o el
        ejercicio de derechos, puedes contactar con el Club:
      </Text>
      <Text fontWeight="semibold" color={headingColor}>
        Club Atlético Pacífico
      </Text>
      <Text>
        Sitio web:{' '}
        <ExternalLink href="https://pacificobb.com">https://pacificobb.com</ExternalLink>
      </Text>
      <Text>Correo electrónico: clubpacificobahia@gmail.com</Text>
      <Text>Domicilio: Charlone 285, Bahía Blanca (C.P. 8000), Provincia de Buenos Aires, Argentina</Text>
    </VStack>
  );
}

export default PrivacyPolicyContent;
